/**
 * Per-card impression tracking for the related-properties slider (#717,
 * phase 1 — widget-local only). When a card first becomes visible in the
 * Embla viewport, one impression is emitted per property per mount:
 *
 * - a `hr-widget-impression` CustomEvent dispatched on the widget's shadow
 *   host (bubbles + composed, so host-page listeners hear it), and
 * - a push to `window.dataLayer` when the host page defines one (GTM/GA).
 *
 * No network requests — the host page decides what to do with the events.
 * Naming is deliberate: these are impressions (card entered the carousel
 * viewport), not views or engagement.
 */
import { useCallback, useEffect, useRef } from 'react'
import type { CarouselApi } from '../../components/ui/carousel'
import type { ExploreProperty } from '../explorer/data'

export interface ImpressionContext {
  widgetType: string
  template: string
  widgetId: string
  feedId: number
}

export interface ImpressionDetail extends ImpressionContext {
  propertyId: ExploreProperty['id']
  position: number
}

export const IMPRESSION_EVENT = 'hr-widget-impression'

/**
 * Pure impression recorder: dedups by property id for its lifetime (one
 * tracker = one widget mount) and emits to the given targets. Returns the
 * details it emitted so callers/tests can observe without listening.
 */
export function createImpressionTracker(ctx: ImpressionContext) {
  const seen = new Set<ExploreProperty['id']>()

  return {
    record(
      inViewIndices: number[],
      properties: ExploreProperty[],
      hostEl: Element | null,
      dataLayer?: unknown,
    ): ImpressionDetail[] {
      const emitted: ImpressionDetail[] = []
      for (const position of inViewIndices) {
        const property = properties[position]
        if (!property || seen.has(property.id)) continue
        seen.add(property.id)
        const detail: ImpressionDetail = { ...ctx, propertyId: property.id, position }
        emitted.push(detail)
        hostEl?.dispatchEvent(
          new CustomEvent(IMPRESSION_EVENT, { detail, bubbles: true, composed: true }),
        )
        if (Array.isArray(dataLayer)) {
          dataLayer.push({
            event: 'hr_widget_impression',
            hr_widget_type: detail.widgetType,
            hr_template: detail.template,
            hr_widget_id: detail.widgetId,
            hr_feed_id: detail.feedId,
            hr_property_id: detail.propertyId,
            hr_position: detail.position,
          })
        }
      }
      return emitted
    },
  }
}

/**
 * Glue between the shared Carousel's `setApi` and the tracker. Attach the
 * returned `markerRef` to any element inside the widget (used to find the
 * shadow host) and pass `setApi` to `<Carousel>`. Listener hygiene mirrors
 * the shared carousel's own rules: the `slidesInView` callback is off'd on
 * unmount and when the api instance is replaced.
 */
export function useSlideImpressions(
  ctx: ImpressionContext,
  properties: ExploreProperty[],
) {
  const markerRef = useRef<HTMLDivElement | null>(null)
  const trackerRef = useRef<ReturnType<typeof createImpressionTracker> | null>(null)
  const propertiesRef = useRef(properties)
  propertiesRef.current = properties
  const cleanupRef = useRef<(() => void) | null>(null)

  if (!trackerRef.current) trackerRef.current = createImpressionTracker(ctx)

  const setApi = useCallback((api: CarouselApi) => {
    cleanupRef.current?.()
    cleanupRef.current = null
    if (!api) return
    const handler = () => {
      const root = markerRef.current?.getRootNode()
      const hostEl = root instanceof ShadowRoot ? root.host : null
      trackerRef.current?.record(
        api.slidesInView(),
        propertiesRef.current,
        hostEl,
        typeof window !== 'undefined'
          ? (window as { dataLayer?: unknown }).dataLayer
          : undefined,
      )
    }
    api.on('slidesInView', handler)
    // Emit for the initially visible slides — `slidesInView` only fires on
    // change, and the first cards are visible before any scroll happens.
    handler()
    cleanupRef.current = () => api.off('slidesInView', handler)
  }, [])

  useEffect(() => () => cleanupRef.current?.(), [])

  return { setApi, markerRef }
}
